import React, { Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { CContainer, CFade } from '@coreui/react';
import { fetchRoles } from '../Features/Roles/Select/actions';
import { TheSidebar, TheFooter, TheHeader } from './index';

import routes from '../core/routes';
import { isSettingEnabled } from '../core/helpers/initiatives';
import RouteWithProtect from '../core/RouteWithProtect';
import useGlobalSelectionsContext from '../core/hooks/useGlobalSelectionsContext';
import Preloader from '../Shared/Preloader';
import { getUserProfile } from '../Features/Auth/actions';
import PetitionShortCode from '../Features/PetitionShortCode';
import { homeRoutes } from '../core/constants/auth';

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
);

const TheLayout = () => {
  const dispatch = useDispatch();
  const darkMode = useSelector((state) => state.base.darkMode);
  const { loading: isProfileOnLoading } = useSelector(({ auth }) => auth.auth);
  const {
    user,
    initiativeSettings,
    isInitiativeEmpty,
    initiativeId,
    isMainDataLoaded,
  } = useGlobalSelectionsContext();

  const { pathname } = useLocation();
  const isSignUp = React.useMemo(() => pathname.includes('sign-up'), [pathname]);

  const classes = React.useMemo(
    () =>
      classNames('c-app c-default-layout', darkMode && 'c-dark-theme', isSignUp && 'signup-page'),
    [darkMode, isSignUp],
  );

  React.useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(getUserProfile(initiativeId));
  }, [dispatch, initiativeId]);

  const withoutInitiativeProtect = React.useCallback(
    (noProtect) => noProtect || !isInitiativeEmpty,
    [isInitiativeEmpty],
  );

  // Redirect to correct page for users with limited page access
  const redirectTo = homeRoutes[user?.role] || '/manage-petitions';

  return (
    <div className={classes}>
      <TheSidebar />
      <div className="c-wrapper">
        <TheHeader />
        <div className="c-body" style={{ position: 'relative' }}>
          <main className="c-main d-flex flex-column flex-grow-1">
            {isProfileOnLoading && <Preloader />}
            {isMainDataLoaded ? (
              <CContainer fluid className="h-100">
                <Suspense fallback={loading}>
                  <Switch>
                    <Route path="/p/:short_code" component={PetitionShortCode} />
                    {routes.map(
                      (route) =>
                        route.component &&
                        isSettingEnabled(route.initiativeSettingKey, initiativeSettings) &&
                        withoutInitiativeProtect(route.isWithoutInitiative) && (
                          <Route
                            key={route.name}
                            path={route.path}
                            exact={route.exact}
                            name={route.name}
                            render={(props) => (
                              <CFade className="h-100">
                                <RouteWithProtect routePermissions={route}>
                                  <route.component {...props} />
                                </RouteWithProtect>
                              </CFade>
                            )}
                          />
                        ),
                    )}
                    <Redirect from="/" to={redirectTo} />
                  </Switch>
                </Suspense>
              </CContainer>
            ) : (
              <Preloader staticLoader />
            )}
          </main>
        </div>
        <TheFooter />
      </div>
    </div>
  );
};

export const TheLayoutRouter = {
  path: '/',
  component: TheLayout,
  routes,
};

export default TheLayout;
